import { CAROUSEL_DEFAULT_OPTS, CAROUSEL_HERO_NEWS_OPTS } from '../constants/carousel-args';
import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { module as Module } from 'modujs';
import { $prefersReducedMotion } from '../stores/prefers-reduced-motion';

export default class Carousel extends Module {
    static CONTROLS_CLASS = 'has-slider-controls';

    constructor(m) {
        super(m);

        // Bindings
        this.onUpdateTabIndex = this.onUpdateTabIndex.bind(this);
        this.onClearTabIndex = this.onClearTabIndex.bind(this);
        this.onPrefersReducedMotionChange = this.onPrefersReducedMotionChange.bind(this);
        this.play = this.play.bind(this);
        this.pause = this.pause.bind(this);

        // UI
        this.$swiper = this.$('swiper')[0];
        this.$prevButton = this.$('prev')[0];
        this.$nextButton = this.$('next')[0];
        this.$pagination = this.$('pagination')[0];

        // Data
        this.swiper = null;
        this.type = this.getData('type');
        this.isInview = false;
    }

    // =============================================================================
    // Lifecycle
    // =============================================================================
    init() {
        this.unbindPrefersReducedMotion = $prefersReducedMotion.subscribe(
            this.onPrefersReducedMotionChange
        );
    }

    destroy() {
        super.destroy();
        this.destroySwiper();
        this.unbindPrefersReducedMotion?.();
    }

    // =============================================================================
    // Callbacks
    // =============================================================================
    onInview({ way }) {
        if (way === 'enter' && !this.isInview) {
            this.play();
            this.isInview = true;
        } else if (way === 'leave' && this.isInview) {
            this.pause();
            this.isInview = false;
        }
    }

    onUpdateTabIndex(args) {
        const { slides, activeIndex } = args;
        const $currentSlide = slides[activeIndex];
        const $otherSlides = slides.filter(($slide) => $slide !== $currentSlide);

        $otherSlides.forEach(($slide) => {
            const $tabbulableElements = $slide.querySelectorAll(
                'a, button, input, select, textarea, [tabindex]'
            );
            $tabbulableElements.forEach(($element) => {
                $element.setAttribute('tabindex', '-1');
                $element.removeEventListener('focus', this.pause);
                $element.removeEventListener('blur', this.play);
            });
        });

        const $tabbulableElements = $currentSlide.querySelectorAll(
            'a, button, input, select, textarea, [tabindex]'
        );
        $tabbulableElements.forEach(($element) => {
            $element.removeAttribute('tabindex');
            $element.addEventListener('focus', this.pause);
            $element.addEventListener('blur', this.play);
        });
    }

    onClearTabIndex(args) {
        const { slides } = args;

        slides.forEach(($slide) => {
            const $tabbulableElements = $slide.querySelectorAll(
                'a, button, input, select, textarea, [tabindex]'
            );
            $tabbulableElements.forEach(($element) => {
                $element.removeAttribute('tabindex');
                $element.removeEventListener('focus', this.pause);
                $element.removeEventListener('blur', this.play);
            });
        });
    }

    onPrefersReducedMotionChange() {
        this.destroySwiper();
        this.initSwiper();
    }

    // =============================================================================
    // Methods
    // =============================================================================
    initSwiper() {
        let argsFunction;
        let modules;

        switch (this.type) {
            case 'hero-news':
                argsFunction = CAROUSEL_HERO_NEWS_OPTS;
                modules = [Navigation, Pagination, Autoplay];
                break;

            default:
                argsFunction = CAROUSEL_DEFAULT_OPTS;
                modules = [Navigation];
                break;
        }

        const args = argsFunction({
            modules,
            $swiper: this.$swiper,
            $prevButton: this.$prevButton,
            $nextButton: this.$nextButton,
            $pagination: this.$pagination,
            isPrefersReducedMotion: $prefersReducedMotion.value
        });

        this.swiper = new Swiper(this.$swiper, { init: false, ...args });
        this.swiper.autoplay?.pause();
        this.swiper.on('slideChange', this.onUpdateTabIndex);
        this.swiper.on('init', this.onUpdateTabIndex);
        this.swiper.on('destroy', this.onClearTabIndex);
        this.swiper.init();
    }

    destroySwiper() {
        this.swiper?.destroy(true, true);
    }

    play() {
        !$prefersReducedMotion.value && this.swiper?.autoplay?.resume();
    }

    pause() {
        !$prefersReducedMotion.value && this.swiper?.autoplay?.pause();
    }
}
